/* eslint-disable react/style-prop-object */
import React, { useEffect, useMemo, useState } from 'react';
import { Empty, Spin } from 'antd';
import axios from 'axios';
import Utils from '../../../../misc/Utils';
import DiagnosticReportUtils from '../../../../misc/DiagnosticReportUtils';
import { IntroSum } from '../../diagnostic-components';

function getAssessmentId(assignmentId, fallbackAssessmentId) {
   return assignmentId !== undefined ? assignmentId : fallbackAssessmentId;
}

function getSummaryDataUrl(params, config) {
   const aid = getAssessmentId(params.assignment_id, params.assessment_id);
   const apiBase = process.env.REACT_APP_API_BRAINEE_DIAGNOSTIC;

   if (params.version === 1) {
      return `${apiBase}/getdiagnosticreportdata?aid=${aid}&sid=${params.section_id}`;
   }

   if (config.summaryApiVersion === 3) {
      return `${apiBase}/v3/getdiagnosticreportdata?aid=${aid}&paid=${params.pre_assignment_id}&sids=${params.section_id}&s=${params.subject_id}&sy=${params.school_year}`;
   }

   return `${apiBase}/v2/getdiagnosticreportdata?aid=${aid}&sids=${params.section_id}&s=${params.subject_id}&sy=${params.school_year}`;
}

function getSummaryPercentUrl(params, config) {
   const aid = getAssessmentId(params.assignment_id, params.assessment_id);
   const apiBase = process.env.REACT_APP_API_BRAINEE_DIAGNOSTIC;

   if (params.version === 1) {
      return `${apiBase}/getdiagnosticsummarypercentdata?aid=${aid}&sid=${params.section_id}`;
   }

   if (config.summaryApiVersion === 3) {
      return `${apiBase}/v3/getdiagnosticsummarypercentdata?aid=${aid}&paid=${params.pre_assignment_id}&sids=${params.section_id}&s=${params.subject_id}&sy=${params.school_year}`;
   }

   return `${apiBase}/v2/getdiagnosticsummarypercentdata?aid=${aid}&sids=${params.section_id}&s=${params.subject_id}&sy=${params.school_year}`;
}

export function SharedMTSummaryReport({ config, ...props }) {
   const [reportData, setReportData] = useState(null);
   const [pcData, setPcData] = useState(null);
   const [summaryStats, setSummaryStats] = useState({ areaStats: {}, total: null });
   const [dataLoading, setDataLoading] = useState(false);

   const {
      version,
      assignment_id,
      assessment_id,
      pre_assignment_id,
      section_id,
      subject_id,
      school_year,
      diagnostic_type,
   } = props;

   useEffect(() => {
      Utils.verifyJwt();
      setDataLoading(true);

      const requestParams = {
         version,
         assignment_id,
         assessment_id,
         pre_assignment_id,
         section_id,
         subject_id,
         school_year,
      };

      axios.get(getSummaryDataUrl(requestParams, config)).then((response) => {
         if (!response.data) {
            setDataLoading(false);
            return;
         }

         const data = response.data;
         const preData = data.filter((item) => item.diagnostic_type === 'pre');
         const postData = data.filter((item) => item.diagnostic_type === 'post');

         const subjectConfigurations = {
            mt: {
               categories: Object.fromEntries(config.summaryAreas.map((area) => [
                  area.key,
                  {
                     fields: Array.from({ length: area.count }, (_, i) => `s${area.start + i}`),
                     mean: area.mean,
                     stdDev: area.stdDev,
                     ranges: area.ranges,
                  },
               ])),
               totalRanges: config.totalRanges,
               setters: {},
            },
         };

         const temp = {};
         subjectConfigurations.mt.setters = Object.fromEntries(
            [...config.summaryAreas.map((a) => a.key), 'total'].map((key) => [key, (value) => { temp[key] = value; }])
         );

         Utils.extractDataForDiagnosticSummaryReport(diagnostic_type, preData, postData, subjectConfigurations.mt);

         axios.get(getSummaryPercentUrl(requestParams, config)).then((percentResponse) => {
            setPcData(percentResponse.data && percentResponse.data[0] ? percentResponse.data[0] : null);
            setSummaryStats({
               areaStats: Object.fromEntries(config.summaryAreas.map((a) => [a.key, temp[a.key]])),
               total: temp.total,
            });
            setReportData(data);
            setDataLoading(false);
         }, () => setDataLoading(false));
      }, () => setDataLoading(false));
   }, [version, assignment_id, assessment_id, pre_assignment_id, section_id, subject_id, school_year, diagnostic_type, config]);

   const contentStandards = useMemo(() => {
      const rows = config.summaryAreas.map((area) => ({
         label: area.label,
         beg1: area.ranges[0][0],
         beg2: area.ranges[0][1],
         bas1: area.ranges[1][0],
         bas2: area.ranges[1][1],
         comp1: area.ranges[2][0],
         comp2: area.ranges[2][1],
         data: summaryStats.areaStats[area.key],
      }));

      rows.push({
         label: 'TOTAL',
         beg1: config.totalRanges[0][0],
         beg2: config.totalRanges[0][1],
         bas1: config.totalRanges[1][0],
         bas2: config.totalRanges[1][1],
         comp1: config.totalRanges[2][0],
         comp2: config.totalRanges[2][1],
         data: summaryStats.total,
      });

      return rows;
   }, [config, summaryStats]);

   const diagnosticDate = useMemo(() => {
      if (!reportData || reportData.length === 0) return null;

      const matchedData = reportData.find((item) => item.diagnostic_type === diagnostic_type);
      return (matchedData || reportData[0]).diagnostic_date;
   }, [reportData, diagnostic_type]);

   if (dataLoading) {
      return <div className='center-empty-space'><Spin size='large' tip='Loading data, please wait...' /></div>;
   }

   if (!reportData || reportData.length === 0) {
      return <div className='center-empty-space'><Empty description='No Data Found' image='../images/brainee_mascot.png' /></div>;
   }

   return (
      <div className='dashboard-item-inner3' style={{ margin: '20px' }}>
         <div style={{ fontFamily: 'calibri' }} id='summary-report'>
            <IntroSum
               grade_level={props.template}
               section={props.section_desc}
               school_name={reportData[0].school_name}
               subject_id={props.subject_id}
               subject={props.subject}
               backgroundColor='#4F81BD'
               diagnostic_date={diagnosticDate}
               diagnostic_type={props.diagnostic_type}
            />

            <p style={{ marginTop: '10pt', marginBottom: '0pt', lineHeight: '115%', fontSize: '12pt' }}><strong>&nbsp;</strong></p>
            <div style={{ pageBreakBefore: 'always' }}></div>
            <p style={{ textAlign: 'center', fontSize: '14pt' }}><strong>Summary of Results for {props.subject}</strong></p>
            <p style={{ textAlign: 'center' }}><strong>{props.diagnostic_type === 'post' ? 'POST TEST' : 'PRE TEST'}</strong></p>
            <p style={{ marginTop: '0pt', marginBottom: '0pt' }}><strong>MEAN LEVEL OF SCORES IN THE SUBJECT AREAS</strong></p>
            <table style={{ width: '100%', border: '0.75pt solid #cccccc', borderCollapse: 'collapse' }}>
               <tbody>
                  {DiagnosticReportUtils.generateMeandLevelHeader()}
                  {contentStandards.map((row) => (
                     <React.Fragment key={`mean-${row.label}`}>
                        {DiagnosticReportUtils.generateMeanLevelRow(row.label, row.data, row.label === 'TOTAL')}
                     </React.Fragment>
                  ))}
               </tbody>
            </table>

            <p style={{ marginTop: '0pt', marginBottom: '10pt', lineHeight: '115%', fontSize: '12pt' }}><strong>&nbsp;</strong></p>
            <table style={{ width: '100%', border: '0.75pt solid #cccccc', borderCollapse: 'collapse' }}>
               <tbody>
                  <tr style={{ height: '14.5pt' }}>
                     <td style={{ width: '39.3%', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '4.9pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ textAlign: 'center', marginBottom: '0pt' }}><strong><span style={{ color: '#ffffff' }}>Content Standards</span></strong></p></td>
                     <td colSpan={3} style={{ borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ textAlign: 'center', marginBottom: '0pt' }}><strong><span style={{ color: '#ffffff' }}>Beginner</span></strong></p></td>
                     <td colSpan={3} style={{ borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ textAlign: 'center', marginBottom: '0pt' }}><strong><span style={{ color: '#ffffff' }}>Basic</span></strong></p></td>
                     <td colSpan={3} style={{ borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '4.9pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><p style={{ textAlign: 'center', marginBottom: '0pt' }}><strong><span style={{ color: '#ffffff' }}>Competent</span></strong></p></td>
                  </tr>
                  {contentStandards.map((row) => (
                     <React.Fragment key={`std-${row.label}`}>
                        {DiagnosticReportUtils.generateContentStandards(row.label, row.beg1, row.beg2, row.bas1, row.bas2, row.comp1, row.comp2)}
                     </React.Fragment>
                  ))}
               </tbody>
            </table>

            {contentStandards.map((row, i) => (
               <React.Fragment key={`freq-${row.label}`}>
                  {DiagnosticReportUtils.generateMTFrequencyRow(row.label, row.data, i + 1)}
               </React.Fragment>
            ))}

            <p style={{ marginTop: '0pt', marginBottom: '10pt', lineHeight: '115%', fontSize: '12pt' }}><strong>&nbsp;</strong></p>
            <p style={{ marginTop: '0pt', marginBottom: '10pt', lineHeight: '115%', fontSize: '12pt' }}><strong>Percent of Correct</strong></p>
            <table style={{ width: '100%', border: '0.75pt solid #cccccc', borderCollapse: 'collapse' }}>
               <tbody>
                  <tr>
                     <td colSpan={2} style={{ borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '4.9pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><div style={{ textAlign: 'center' }}><strong><span style={{ color: '#ffffff' }}>LEARNING COMPETENCY</span></strong></div></td>
                     <td style={{ width: '19.9%', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><div style={{ textAlign: 'center' }}><strong><span style={{ color: '#ffffff' }}>Pre Test Percent Correct for the Level</span></strong></div></td>
                     <td style={{ width: '20.6%', borderStyle: 'solid', borderWidth: '0.75pt', paddingRight: '4.9pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#4F81BD' }}><div style={{ textAlign: 'center' }}><strong><span style={{ color: '#ffffff' }}>Post Test Percent Correct for the Level</span></strong></div></td>
                  </tr>

                  {config.sections.map((section) => (
                     <React.Fragment key={`sec-${section.label}`}>
                        {DiagnosticReportUtils.renderLabel(section.label)}
                        {Array.from({ length: section.end - section.start + 1 }, (_, o) => {
                           const index = section.start + o;
                           const competency = config.competencies.find((x) => x.index === index);
                           return (
                              <React.Fragment key={`mt-pc-${index}`}>
                                 {DiagnosticReportUtils.generateMTPercentCorrectRow(
                                    competency ? competency.label : '',
                                    pcData ? pcData[`pre_s${index}`] : null,
                                    pcData ? pcData[`post_s${index}`] : null,
                                    index
                                 )}
                              </React.Fragment>
                           );
                        })}
                     </React.Fragment>
                  ))}
               </tbody>
            </table>
         </div>
      </div>
   );
}
